/**
 * @file Roles.jsx
 * @description Roles component for displaying and managing roles.
 * @component Roles
 * @exports Roles - Main component for displaying and managing roles.
 */

import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Add } from "@mui/icons-material";
import { TextField, Button, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getUserRoles, editUserRole, addUserRole } from "../../../services/adminModule/roles/roleServices.js";
import { AUTO_COMPLETE, VARIANTS, BUTTON_COLORS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { ROLE_STRINGS, ROLE_KEYS, ROLE_MESSAGES, ROLE_ERRORS, ROLE_TABLE_COLUMNS } from "../../../common/constants/adminModule/roleConstants.js";
import RolesTable from "./RolesTable.jsx";
import AddEditRoleDialog from "./AddEditRoleDialog.jsx";

const Roles = () => {
    const [page, setPage] = useState(0);
    const [userRoles, setUserRoles] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRole, setSelectedRole] = useState(null);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [searchUserRole, setSearchUserRole] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [orderBy, setOrderBy] = useState(ROLE_KEYS.ROLE_NAME);
    const [isAddEditRoleModalOpen, setIsAddEditRoleModalOpen] = useState(false);

    useEffect(() => { fetchRoles(); }, []);
    
    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            setUserRoles(await getUserRoles());
        }
        catch (error) {
            console.error(ROLE_ERRORS.FETCH_ROLES_ERROR, error);
        }
        finally {
            setLoadingRoles(false);
        }
    };

    const { rolesToDisplay, filteredRolesLength } = useMemo(() => {
        const filteredRoles = userRoles.filter((userRole) =>
            userRole.rolename.trim().toLowerCase().includes(searchUserRole.toLowerCase())
        );
        return {
            rolesToDisplay: filteredRoles
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredRolesLength: filteredRoles.length,
        };
    }, [userRoles, searchUserRole, order, orderBy, page, rowsPerPage]);

    const handleAddEditRoleClick = (role = {}) => {
        setSelectedRole(role);
        setIsAddEditRoleModalOpen(true);
    };

    const handleSaveRole = async () => {
        if (!selectedRole) return;
        const roleToSave = {
            ...selectedRole,
        };
        try {
            roleToSave.id ? await editUserRole(roleToSave) : await addUserRole(roleToSave);
            setIsAddEditRoleModalOpen(false);
            setSuccessMessage(roleToSave.id ? ROLE_MESSAGES.ROLE_UPDATED : ROLE_MESSAGES.ROLE_ADDED);
            await fetchRoles();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) { console.error(ROLE_ERRORS.SAVE_ROLE_ERROR, error); }
    };

    const handleInputChange = (field) => (e) =>
        setSelectedRole({ ...selectedRole, [field]: e.target.value });

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    return (
        <>
            {successMessage && 
                <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                    {successMessage}
                </Alert>
            }
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={SIZES.GROW}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchUserRole}
                        variant={VARIANTS.OUTLINED}
                        label={ROLE_STRINGS.SEARCH_ROLES}
                        autoComplete={AUTO_COMPLETE.OFF}
                        onChange={e => setSearchUserRole(e.target.value)}
                    />
                </Grid>
                <Grid size={SIZES.AUTO}>
                    <Button
                        startIcon={<Add />}
                        variant={VARIANTS.CONTAINED}
                        color={BUTTON_COLORS.PRIMARY}
                        onClick={() => handleAddEditRoleClick({})}
                    >
                        {ROLE_STRINGS.ADD_ROLE}
                    </Button>
                </Grid>
            </Grid>
            <RolesTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                roles={rolesToDisplay}
                rowsPerPage={rowsPerPage}
                columns={ROLE_TABLE_COLUMNS}
                isRolesLoading={loadingRoles}
                onEdit={handleAddEditRoleClick}
                setRowsPerPage={setRowsPerPage}
                handleRequestSort={handleRequestSort}
                filteredRolesLength={filteredRolesLength}
            />
            <AddEditRoleDialog
                onSave={handleSaveRole}
                selectedRole={selectedRole}
                open={isAddEditRoleModalOpen}
                handleInputChange={handleInputChange}
                onClose={() => setIsAddEditRoleModalOpen(false)}
            />
        </>
    );
};

export default Roles;